import React, { useRef, useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import {
  GatsbyImage,
  GatsbyImageProps as IGatsbyImageProps,
} from 'gatsby-plugin-image'
import { AssetFluid } from '@talentinc/gatsby-theme-ecom/types/asset'
import useIsomorphicLayoutEffect from 'react-use/lib/useIsomorphicLayoutEffect'
import { BindToFields, CssEntry } from '../../types/contentfulAttributes'
import { createContentfulClassGetter } from '../../utils/contentful'

interface Props {
  asset: AssetFluid
  className?: string
  imgClassName?: string
  objectFit?: IGatsbyImageProps['objectFit']
  GatsbyImageProps?: Partial<
    Omit<IGatsbyImageProps, 'image' | 'alt' | 'imgClassName'>
  >
  css?: CssEntry[] | null
}

const Image: React.FC<Props> = ({
  asset,
  className,
  imgClassName,
  objectFit,
  GatsbyImageProps = {},
  css,
}) => {
  const { classes, cx } = useStyles({ asset })
  const [loading, setLoading] = useState<'eager' | 'lazy'>('lazy')
  const imageRef = useRef<HTMLDivElement | null>(null)
  const applyContentfulStyles = createContentfulClassGetter(css ?? null)

  const { loading: loadingProp, ...restGatsbyImageProps } = GatsbyImageProps

  useIsomorphicLayoutEffect(() => {
    const imageRect = imageRef.current?.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    let isAboveTheFold = false

    if (imageRect) {
      isAboveTheFold = imageRect.top + imageRect.height < viewportHeight
    }

    if (isAboveTheFold) {
      setLoading('eager')
    }
  }, [])

  // SVGs do not have gatsbyImageData, so we must use a plain img
  if (!asset.gatsbyImageData) {
    const { width, height } = asset.file?.details?.image ?? {}
    return (
      <img
        src={asset.file.url}
        alt={asset.description}
        className={className}
        width={width}
        height={height}
      />
    )
  }

  return (
    // Maybe use a forwardRef instead?
    <div
      ref={imageRef}
      className={applyContentfulStyles?.(BindToFields.WrapperImage)}
    >
      <GatsbyImage
        image={asset.gatsbyImageData}
        alt={asset.description}
        className={cx(classes.gatsbyImageWrapper, className)}
        imgClassName={imgClassName}
        objectFit={objectFit}
        loading={loadingProp ?? loading}
        {...restGatsbyImageProps}
      />
    </div>
  )
}
const useStyles = makeStyles<{ asset: any }>()((theme, { asset }) => {
  const maxHeight =
    asset.gatsbyImageData?.layout === 'constrained'
      ? Math.min(asset.gatsbyImageData.height, asset.file.details.image.height)
      : 'auto'

  const maxWidth =
    asset.gatsbyImageData?.layout === 'constrained'
      ? Math.min(asset.gatsbyImageData.width, asset.file.details.image.width)
      : 'auto'

  return {
    // Setting styles here instead of inline so that parent components and
    // override without using !important
    gatsbyImageWrapper: {
      // gatsby-image defaulted to block, gatsby-plugin-image defaults to
      // inline-block. We want to maintain compatibility with gatsby-image, as
      // inline-block doesn't work with spacer <br />s in posts.
      '&&': {
        display: 'block',
      },
      // By setting the width in the query for the image, there is a chance that
      // the width/height will be higher than the source image. Luckily we have
      // the source image dimensions on the file object. In all cases, we want
      // the lowest resolution.
      maxHeight: maxHeight,
      maxWidth: maxWidth,
      // @ts-ignore
      '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
        '& picture': {
          position: 'static !important' as 'static',
        },
      },
    },
  }
})

export default Image
